import type { TQuizMode } from '@/store/quiz/state'
import type { Study } from '@pocketprep/types'

export const uniqBy = <T>(fn: (item: T) => unknown, list: T[]) => {
    const set = new Set()
    const result = []
    let idx = 0
    let appliedItem, item

    while (idx < list.length) {
        item = list[idx]
        appliedItem = fn(item)
        if (!set.has(appliedItem)) {
            set.add(appliedItem)
            result.push(item)
        }
        idx += 1
    }
    return result
}

export const difference = <T>(first: T[], second: T[]) => {
    const out = []
    let idx = 0
    const firstLen = first.length
    const secondSet = new Set(second)

    while (idx < firstLen) {
        if (!secondSet.has(first[idx])) {
            secondSet.add(first[idx])
            out.push(first[idx])
        }
        idx += 1
    }
    return out
}

export const buildListDifference = <T>(first: T[], second: T[]) => {
    const differences = []

    for (let i = 0; i < Math.max(first.length, second.length); i++) {
        if (first[i] !== second[i]) {
            differences.push({
                position: i,
                firstValue: first[i],
                secondValue: second[i],
            })
        }
    }

    return differences
}

export const isValidEmail = (email?: string) => {
    // Safari Browser versions < 16.4 do not support lookbehinds so our email.match
    // surfaces SyntaxError: Invalid regular expression: invalid group specifier name
    // We can be to keep our regex minus the lookbehind +(?<!\.)
    // And add a check to make sure the last character before the @ is not a .

    // previous regex email?.match(/[A-Z0-9._%+-]+(?<!\.)@(?:(?!\.\.)[A-Z0-9.-])+\.[A-Z]{2,10}/i)
    if (!email) {
        return false
    }
    const emailBody = email.split('@')[0]
    const lastCharOfEmailBody = emailBody[emailBody.length - 1]
    return !!(
        email.match(/[A-Z0-9a-z._%+-]+@(?:(?!\.\.)[A-Za-z0-9.-])+\.[A-Za-z]{2,63}$/) && lastCharOfEmailBody !== '.'
    )
}

export const isValidPassword = (password?: string) => {
    if (!password) {
        return false
    }

    if (password.length < 10) {
        return false
    }

    // check that the string has an uppercase letter, a lowercase letter, and a number (digit)
    return /[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password)
}

export const passwordPolicyErrors = (isShowingPasswordErrorHeader: boolean, password?: string): string[] => {
    const errors = []

    if (!password) {
        return [
            `${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} be at least 10 characters`,
            `${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include an uppercase letter`,
            `${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include a lowercase letter`,
            `${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include a number`,
        ]
    }

    if (password.length < 10) {
        errors.push(`${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} be at least 10 characters`)
    }

    if (!/[A-Z]/.test(password)) {
        errors.push(`${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include an uppercase letter`)
    }

    if (!/[a-z]/.test(password)) {
        errors.push(`${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include a lowercase letter`)
    }

    if (!/\d/.test(password)) {
        errors.push(`${isShowingPasswordErrorHeader ? 'Must' : 'Password must'} include a number`)
    }

    return errors
}

export const timeOfDay = (): 'midnight' | 'early' | 'morning' | 'afternoon' | 'evening' => {
    const hour = new Date().getHours()
    const minute = new Date().getMinutes()

    if (hour <= 4 && minute < 30) {
        return 'midnight'
    } else if (hour < 7) {
        return 'early'
    } else if (hour < 12) {
        return 'morning'
    } else if (hour < 17) {
        return 'afternoon'
    } else if (hour < 23) {
        return 'evening'
    } else {
        return 'midnight'
    }
}

export const localISODateString = (): string => {
    // we still have IE users, so avoiding padStart and repeat fcns
    // NOTE: Array() inserts between array elements, need + 1
    const zeroPadded = (num: number, digits = 2): string =>
        num.toString().length < digits + 1
            ? Array(digits + 1 - num.toString().length).join('0') + `${num}`
            : `${num}`
    const now = new Date()
    const tzHours = Math.floor(Math.abs(now.getTimezoneOffset() / 60))
    const tzMins = Math.abs(now.getTimezoneOffset() % 60)

    return `${now.getFullYear()}`
        + '-' + zeroPadded(now.getMonth() + 1) // January = 0
        + '-' + zeroPadded(now.getDate())
        + 'T' + zeroPadded(now.getHours())
        + ':' + zeroPadded(now.getMinutes())
        + ':' + zeroPadded(now.getSeconds())
        + '.' + zeroPadded(now.getMilliseconds(), 3)
        + ((now.getTimezoneOffset() > 0) ? '-' : '+')        // offset is GMT - local time so flip sign
        + zeroPadded(tzHours)
        + ':' + zeroPadded(tzMins)
}

export const getScoreRange = (score: number) => {
    if (score <= 50) {
        return 'low'
    } else if (score < 75) {
        return 'medium'
    } else {
        return 'high'
    }
}

export const tutorInputMaxLength = 2000

export const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

export const copyText = (url: string) => {
    navigator.clipboard.writeText(url)
}

export const allowedQuestionsPerMinute = 10

export const getHoursMinutesSeconds = (durationSeconds: number) => {
    const hourSeconds = durationSeconds || 0
    const hours = Math.floor(hourSeconds / 3600)
    const minuteSeconds = hourSeconds % 3600
    const minutes = Math.floor(minuteSeconds / 60)
    const seconds = minuteSeconds % 60

    return {
        hours,
        minutes,
        seconds,
    }
}

export const formatStudyTime = (durationSeconds: number) => {
    const { hours, minutes, seconds } = getHoursMinutesSeconds(durationSeconds)

    return hours
        ? `${hours}h ${minutes}m`
        : minutes
            ? `${minutes}m ${seconds}s`
            : `${seconds}s`
}

export const getCookie = (cookieName: string) => {
    const cookieLib = document.cookie.split('; ').reduce((acc, cookie) => {
        const [ name, value ] = cookie.split('=')
        if (value && name) {
            acc[name] = value
        }
        return acc
    }, {} as Record<string, string>)
    return cookieLib[cookieName]
}

export const setCookie = ({ name, value }: {
    name: string
    value: string
}) => {
    const cookieDomain = import.meta.env.VUE_APP_COOKIE_DOMAIN
    if (cookieDomain) {
        document.cookie = `${name}=${value}; domain=${cookieDomain}`
    } else {
        document.cookie = `${name}=${value}`
    }
}

export const clearCookie = (cookieName: string) => {
    const cookieDomain = import.meta.env.VUE_APP_COOKIE_DOMAIN
    if (cookieDomain) {
        document.cookie = `${cookieName}=; domain=${cookieDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
    } else {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
    }
}

export type TFilterOption = {
    id: string
    label: string
    enabled: boolean
}

export type TRow = {
    id: string
    source: string
    bundleId: string
    name: string
    nameSubtext: string
    planName: string
    planCost: string
    billingCycle: string
    willAutoRenew: boolean
    renewsExpired: string
    paymentPlatform: string
    paymentSubtext: string
    canEdit: boolean
    expired: boolean
}

export const environment = (() => {
    if (import.meta.env.VUE_APP_PARSE_URL) {
        if (import.meta.env.VUE_APP_PARSE_URL.includes('localhost')) {
            return 'localhost'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('test')) {
            return 'test'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('development')) {
            return 'development'
        } else if (import.meta.env.VUE_APP_PARSE_URL.includes('staging')) {
            return 'staging'
        }
    }
    return 'production'
})()

export const isSupportWindow = () => {
    return window.location.origin.includes('support')
}

export const formatDate = (date: Date, interval: string, intervalCount: number) => {
    const initialDate = date.getDate()
    if (interval === 'year') {
        date.setFullYear(date.getFullYear() + intervalCount)
    } else if (interval === 'month') {
        date.setMonth(date.getMonth() + intervalCount)
    }

    if (date.getDate() !== initialDate) { // If the dates don't match, it means we rolled into the next month
        date.setDate(0) // Setting the date to 0 changes the date to the last day of the previous month
    }

    const newDate = String(date.getMonth() + 1) + '/' + String(date.getDate()) + '/' + String(date.getFullYear())
    return newDate ? newDate : null
}

export type TQueryParamKeys = 'amplitudeSessionId' | 'bundleId' | 'code' | 'email' | 'examGuid' | 'groupCode' | 'ulPath'
    | 'license' | 'platform' | 'priorScreenName' | 'productIndex' | 'redirect' | 'referral' | 'upgrade' | 'userData'
export type PPQueryParams = Partial<Record<TQueryParamKeys, string>>

// Map quiz string types to numeric types
export const getNumericQuizMode = (mode: TQuizMode) => {
    const quizModeMap = {
        'qotd': '-1',
        'quick10': '0',
        'missed': '2',
        'weakest': '3',
        'timed': '4',
        'mockExam': '5',
        'levelUp': '6',
        'recallChallenge': '7',
        'custom': '10',
        'assignment': '11',
        'concept': '12',
    }

    return quizModeMap[mode] || 'unknown'
}

export const stripHtmlTags = (string?: string) => {
    if (string) {
        const div = document.createElement('div')
        div.innerHTML = string
        return div.textContent || ''
    }
    return ''
}

export const examVersionNativeAppName = (examNativeAppName?: string) => {
    // this will allow exam version titles in components to still make sense
    // if for whatever reason no examNativeAppName is passed in by components
    return examNativeAppName ? `of ${examNativeAppName}` : ''
}

export const planTypeLength = (daysInSub: number) => {
    return daysInSub > 362
        ? 'Yearly'
        : daysInSub > 87
            ? 'Quarterly'
            : 'Monthly'
}

export const generateAnswerSeeds = (questions: Study.Class.QuestionJSON[]) => {
    const hasMPMCQuestion = questions.some(question => question.type === 'Multi-Part Multiple Choice')
    // MPMC questions can have up to 80 choices. If the array of questions has at least
    // one MPMC question then we need to have 80 indexes in our answerSeeds arrays
    const maxChoices = hasMPMCQuestion ? 80 : 10
    return Array.from({ length: questions.length }).map(() => {
        return Array.from({ length: maxChoices }, (_, i) => i).sort(() => Math.random() - 0.5)
    })
}
